"use client";

import { useMemo } from "react";
import { useClerk, useUser } from "@clerk/nextjs";
// The hook exposes Clerk's custom-flow resource API in @clerk/nextjs 7.
import { useSignIn } from "@clerk/nextjs/legacy";
import type { ClerkTicketSession } from "~/components/auth/loginCodeClient";

/**
 * What the sign-in code needs from Clerk on a surface inside a Clerk
 * provider: who the browser is signed in as, the ticket sign-in, and
 * sign-out. Null until Clerk has loaded.
 */
export function useClerkTicketSession(): ClerkTicketSession | null {
  const { isLoaded, signIn, setActive } = useSignIn();
  const { isLoaded: userLoaded, user } = useUser();
  const { signOut } = useClerk();
  const signedInUserId = user?.id ?? null;
  const signedInEmail = user?.primaryEmailAddress?.emailAddress ?? null;
  return useMemo(() => {
    if (!isLoaded || !userLoaded || !signIn || !setActive) return null;
    return {
      signedInUserId,
      signedInEmail,
      async signInWithTicket(ticket: string) {
        const result = await signIn.create({ strategy: "ticket", ticket });
        if (result.status !== "complete" || !result.createdSessionId) {
          throw new Error("Sign-in could not be completed.");
        }
        await setActive({ session: result.createdSessionId });
      },
      async signOut() {
        await signOut();
      },
    };
  }, [
    isLoaded,
    userLoaded,
    signIn,
    setActive,
    signedInUserId,
    signedInEmail,
    signOut,
  ]);
}
